import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import PartFormDialog from '../detailDialogForms/PartFormDialog';
import MaterialCostTypeDialog from '../detailDialogForms/MaterialCostTypeDialog';
import ItemReplacementDetailsDialog from '../detailDialogForms/ItemReplacementDetailsDialog';
import {
    useAddWarrantyClaimsItemMutation,
    useGetWarrantyClaimsByIdQuery,
    useGetWarrantyClaimsItemListQuery,
    useUpdateWarrantyClaimsItemMutation,
    useDeleteWarrantyClaimsItemMutation,
    useCalculateWarrantyClaimTotalsQuery
} from '../../../../../../../api';
import { useLazyGetClaimCardPdfDataQuery, useLazyGetItemReplacementDetailsQuery } from '../../../../../../../api/endpoints/warrantyClaims';
import { selectSiteUseId } from '../../../../../../../store/slices/userParamsSlice';
import toastService from '../../../../../../../utils/toastService';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { useGetStockByPartCodeQuery } from '../../../../../../../api/endpoints/dealerPartsCatalog';
import PdfGenerationService from '../../../../../../../utils/pdfGenerationService';
import { usePermissions } from '../../../../../../../hooks/usePermissions';
import { WarrantyClaimStatus } from '../../../../../../../utils/warrantyClaimsEnums';

const DISABLED_STATUSES = [WarrantyClaimStatus.ACCEPTED, WarrantyClaimStatus.REJECTED];

const PartListTable = ({ claimId, dealerId, dealerAddressId, authorizationCheck = false, warrType }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const { hasDealerRole, hasServiceDealerRole, hasSalesDealerRole } = usePermissions();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingPart, setEditingPart] = useState(null);
    const siteUseId = useSelector(selectSiteUseId);
    const toastRef = useRef(null);
    const [triggerGetPdfData, { isLoading: isLoadingPdf }] = useLazyGetClaimCardPdfDataQuery();
    const [triggerGetReplacementDetails, { data: replacementDetailsData, isLoading: isLoadingReplacementDetails }] = useLazyGetItemReplacementDetailsQuery();
    const pdfService = useMemo(() => new PdfGenerationService(), []);

    const [replacementDetailsVisible, setReplacementDetailsVisible] = useState(false);
    const [selectedReplacementRow, setSelectedReplacementRow] = useState(null);
    const [materialCostTypeDialogVisible, setMaterialCostTypeDialogVisible] = useState(false);
    const [selectedMaterialCostTypeRow, setSelectedMaterialCostTypeRow] = useState(null);

    const [lazyParams, setLazyParams] = useState({
        rows: 10,
        sortField: null,
        sortOrder: null
    });

    const {
        data: apiWarrantyClaimsItemList,
        isLoading,
        refetch
    } = useGetWarrantyClaimsItemListQuery({
        warrClaimId: claimId,
        PageSize: 1000,
        PageNumber: 1,
        LanguageId: getCurrentLanguageId()
    });
    const { data: warrantyClaimGetById } = useGetWarrantyClaimsByIdQuery({ id: claimId, languageId: getCurrentLanguageId() });
    const { refetch: refetchTotals } = useCalculateWarrantyClaimTotalsQuery({ warrantyClaimId: claimId }, { skip: !claimId });
    const [addWarrantyClaimsItem] = useAddWarrantyClaimsItemMutation();
    const [updateWarrantyClaimsItem] = useUpdateWarrantyClaimsItemMutation();
    const [deleteWarrantyClaimsItem] = useDeleteWarrantyClaimsItemMutation();

    // Servis Bülteni kontrolü
    const isServiceBulletin = warrantyClaimGetById?.warrType === 'B';
    const isButtonsDisabled = DISABLED_STATUSES.includes(warrantyClaimGetById?.status) || warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING || warrantyClaimGetById?.status === WarrantyClaimStatus.RETURNED_TO_KARSAN || isServiceBulletin;
    const disabledForRole = (hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole()) && warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING;
    useEffect(() => {
        if (claimId) {
            refetch();
        }
    }, [claimId, refetch]);
    const sortedData = useMemo(() => {
        const allItems = apiWarrantyClaimsItemList?.data?.items || [];

        if (!lazyParams.sortField || lazyParams.sortOrder === 0) {
            return allItems;
        }

        return [...allItems].sort((a, b) => {
            let aValue = a[lazyParams.sortField];
            let bValue = b[lazyParams.sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return lazyParams.sortOrder;
            if (bValue == null) return -lazyParams.sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return lazyParams.sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -lazyParams.sortOrder;
            if (aValue > bValue) return lazyParams.sortOrder;
            return 0;
        });
    }, [apiWarrantyClaimsItemList?.data?.items, lazyParams.sortField, lazyParams.sortOrder]);

    //Stok Bilgisi
    const StockCell = ({ itemCode, branchCode, branchSiteCode }) => {
        const { data: stockResponse, isLoading } = useGetStockByPartCodeQuery(
            {
                partCode: itemCode,
                bosBranchCode: branchCode,
                bosBranchSiteCode: branchSiteCode
            },
            { skip: !itemCode }
        );

        if (isLoading) return <span>-</span>;

        return <span>{stockResponse?.data?.items?.[0]?.quantity ?? '0'}</span>;
    };

    const handleNewPart = () => {
        setEditingPart(null);
        setDialogVisible(true);
    };

    const handleSave = async (data) => {
        try {
            let result;
            const dataWithSiteUseId = {
                ...data,
                siteUseId: siteUseId || 0
            };

            if (data?.id) {
                result = await updateWarrantyClaimsItem(dataWithSiteUseId).unwrap();
            } else {
                result = await addWarrantyClaimsItem(dataWithSiteUseId).unwrap();
            }

            if (result) {
                refetch();
                refetchTotals();
                setDialogVisible(false);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Parça kaydedilirken hata:', error);
        }
    };

    const handleEdit = (rowData) => {
        setEditingPart(rowData);
        setDialogVisible(true);
    };

    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const handleDelete = async (part) => {
        try {
            const result = await deleteWarrantyClaimsItem(part.id).unwrap();
            if (result) {
                refetch();
                refetchTotals();
                setDialogVisible(false);
                toastService.success(th('warrantyClaims.detail.partTable.deleteSuccess'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Parça silinirken hata:', error);
            toastService.error(th('warrantyClaims.detail.partTable.deleteError'));
            return false;
        }
    };

    // PDF görüntüleme
    const handleViewPdf = useCallback(
        async (rowData) => {
            const warrClaimsNo = warrantyClaimGetById?.warrClaimsNo;
            const itemCode = rowData.itemCode;

            if (!warrClaimsNo || !itemCode) {
                toastRef.current?.show({
                    severity: 'error',
                    summary: th('common.error'),
                    detail: th('warrantyClaims.pdf.missingData'),
                    life: 3000
                });
                return;
            }

            try {
                const result = await triggerGetPdfData({ warrClaimsNo, itemCode }).unwrap();

                if (result?.success && result?.data) {
                    // Dil çevirilerini PDF servisine gönder
                    const translations = {
                        title: th('warrantyClaims.pdf.title'),
                        service: th('warrantyClaims.pdf.service'),
                        claimNo: th('warrantyClaims.pdf.claimNo'),
                        faultKm: th('warrantyClaims.pdf.faultKm'),
                        repairKm: th('warrantyClaims.pdf.repairKm'),
                        chassisNo: th('warrantyClaims.pdf.chassisNo'),
                        warrantyStart: th('warrantyClaims.pdf.warrantyStart'),
                        plateNo: th('warrantyClaims.pdf.plateNo'),
                        repairNoDate: th('warrantyClaims.pdf.repairNoDate'),
                        faultDate: th('warrantyClaims.pdf.faultDate'),
                        repairDate: th('warrantyClaims.pdf.repairDate'),
                        partNo: th('warrantyClaims.pdf.partNo'),
                        partDescription: th('warrantyClaims.pdf.partDescription'),
                        quantity: th('warrantyClaims.pdf.quantity'),
                        description: th('warrantyClaims.detail.partDialog.description'),
                        causeOfFailure: th('warrantyClaims.pdf.causeOfFailure'),
                        customerStatement: th('warrantyClaims.pdf.customerStatement'),
                        authorizedService: th('warrantyClaims.pdf.authorizedService'),
                        stampSignature: th('warrantyClaims.pdf.stampSignature'),
                        serviceStamp: th('warrantyClaims.pdf.serviceStamp'),
                        authorizedSignature: th('warrantyClaims.pdf.authorizedSignature'),
                        date: th('warrantyClaims.pdf.date')
                    };

                    const pdfDoc = pdfService.generateClaimCardPdf(result.data, translations, { halfPage: true });
                    pdfService.viewPdf(pdfDoc);
                } else {
                    toastRef.current?.show({
                        severity: 'error',
                        summary: th('common.error'),
                        detail: th('warrantyClaims.pdf.pdfError'),
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('PDF oluşturma hatası:', error);
            }
        },
        [triggerGetPdfData, pdfService, th, warrantyClaimGetById?.warrClaimsNo]
    );
    const handleReplacementDetails = async (rowData) => {
        const chassisNo = warrantyClaimGetById?.chassisNo || '';
        const itemCode = rowData.itemCode || '';

        if (!chassisNo || !itemCode) {
            toastService.warn(th('warrantyClaims.detail.partTable.replacementDetailsMissingParams'));
            return;
        }

        setSelectedReplacementRow(rowData);
        setReplacementDetailsVisible(true);

        try {
            await triggerGetReplacementDetails({ ChassisNo: chassisNo, ItemCode: itemCode, LanguageId: getCurrentLanguageId() });
        } catch {
            setReplacementDetailsVisible(false);
            setSelectedReplacementRow(null);
        }
    };

    const handleOpenMaterialCostTypeDialog = (rowData) => {
        setSelectedMaterialCostTypeRow(rowData);
        setMaterialCostTypeDialogVisible(true);
    };

    const actionBodyTemplate = (rowData) => {
        const isPackageIdValid = rowData.packageId != null && rowData.packageId !== 0;
        const isDisabled = DISABLED_STATUSES.includes(warrantyClaimGetById?.status) || isPackageIdValid;

        return (
            <div className="flex gap-2 justify-content-center">
                <i
                    disabled={isDisabled}
                    onClick={() => !isDisabled && handleEdit(rowData)}
                    className={`pi pi-pencil ${isDisabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-blue-500 hover:text-blue-700'}`}
                    style={{ fontSize: '1.1rem' }}
                    title={th('warrantyClaims.detail.partTable.editTooltip')}
                ></i>
                <i onClick={() => handleViewPdf(rowData)} className="pi pi-file-pdf cursor-pointer text-red-500 hover:text-red-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.pdf.viewPdf')}></i>
                <i
                    onClick={() => handleReplacementDetails(rowData)}
                    className="pi pi-sync cursor-pointer hover:text-blue-700"
                    style={{ fontSize: '1.1rem', color: '#0B3187' }}
                    title={th('warrantyClaims.detail.partTable.replacementDetailsTooltip')}
                ></i>
                {isServiceBulletin && rowData.partType !== 'L' && (
                    <i
                        onClick={() => handleOpenMaterialCostTypeDialog(rowData)}
                        className="pi pi-money-bill cursor-pointer text-orange-500 hover:text-orange-700"
                        style={{ fontSize: '1.1rem' }}
                        title={th('warrantyClaims.detail.partDialog.materialCostType')}
                    ></i>
                )}
            </div>
        );
    };

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    return (
        <div>
            <Toast ref={toastRef} />
            <div className="flex justify-content-start align-items-center mb-3 mt-3">
                <Button disabled={isButtonsDisabled} label={th('warrantyClaims.detail.partTable.newPart')} icon="pi pi-plus" onClick={handleNewPart} />
            </div>
            <DataTable
                value={sortedData}
                className="p-datatable-gridlines"
                showGridlines
                paginator
                rows={lazyParams.rows}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                dataKey="id"
                loading={isLoading}
                emptyMessage={th('warrantyClaims.detail.partTable.noRecordsFound')}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '60rem' }}
            >
                <Column body={actionBodyTemplate} style={{ width: '5%', minWidth: '33px' }} bodyClassName="text-center" headerClassName="text-center" exportable={false} />
                <Column sortable field="itemCode" header={th('warrantyClaims.detail.partTable.partCode')} />
                <Column sortable field="itemCodeDesc" header={th('warrantyClaims.detail.partTable.partDescription')} />
                <Column sortable field="description" header={th('warrantyClaims.detail.partDialog.description')} body={(rowData) => rowData.description || '-'} />
                <Column sortable field="itemPriceCurr" header={th('warrantyClaims.detail.partTable.currency')} />
                <Column sortable field="typeOfWorkName" header={th('warrantyClaims.detail.partTable.workType')} />
                <Column sortable field="mainPart" header={th('warrantyClaims.detail.partTable.mainPart')} body={(row) => (row.mainPart ? th('warrantyClaims.detail.partTable.yes') : th('warrantyClaims.detail.partTable.no'))} />
                <Column sortable field="replacementCount" header={th('warrantyClaims.detail.partTable.replacementCount')} />
                <Column sortable field="warrantyTypeName" header={th('warrantyClaims.detail.partDialog.warrantyType')} />
                <Column field="quantity" header={th('parts.stock')} body={(rowData) => <StockCell itemCode={rowData.itemCode} branchCode={warrantyClaimGetById?.bosBranchCode} branchSiteCode={warrantyClaimGetById?.bosBranchSiteCode} />} />
                {/* isScrap kolonu - status'a göre göster/gizle */}
                {(warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING || warrantyClaimGetById?.status === WarrantyClaimStatus.ACCEPTED || warrantyClaimGetById?.status === WarrantyClaimStatus.REJECTED) && (
                    <Column
                        field="isScrap"
                        header={th('warrantyClaims.detail.partDialog.isScrap')}
                        body={(row) => {
                            return row.isScrap === 1 ? th('warrantyClaims.detail.partDialog.keepPart') : row.isScrap === 2 ? th('warrantyClaims.detail.partDialog.scrapPart') : '-';
                        }}
                    />
                )}
                <Column field="commProfit" header={th('customerAddressBrand.tabs.profit.dialog.label.commercialProfit')} body={(rowData) => formatCurrency(rowData.commProfit)} />
                <Column sortable field="itemPrice" header={th('warrantyClaims.detail.partTable.price')} body={(rowData) => formatCurrency(rowData.itemPrice)} />
                <Column sortable field="quantity" header={th('warrantyClaims.detail.partTable.quantity')} />
                <Column sortable field="totalAmount" header={th('warrantyClaims.detail.partTable.totalAmount')} body={(rowData) => formatCurrency(rowData.totalAmount)} />
                <Column sortable field="acceptedAmount" header={th('warrantyClaims.detail.partTable.acceptedAmount')} body={(rowData) => formatCurrency(rowData.acceptedAmount)} />
            </DataTable>

            <PartFormDialog
                branchCode={warrantyClaimGetById?.bosBranchCode}
                branchSiteCode={warrantyClaimGetById?.bosBranchSiteCode}
                disabledStatuses={isButtonsDisabled || disabledForRole}
                authorizationCheck={authorizationCheck}
                claimId={claimId}
                claimStatus={warrantyClaimGetById?.status}
                warrType={warrantyClaimGetById?.warrType}
                dialogVisible={dialogVisible}
                hideDialog={() => setDialogVisible(false)}
                onSave={handleSave}
                onDelete={handleDelete}
                editingPart={editingPart}
                isAcceptedRejectedTab={
                    warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING ||
                    warrantyClaimGetById?.status === WarrantyClaimStatus.RETURNED_TO_KARSAN ||
                    warrantyClaimGetById?.status === WarrantyClaimStatus.ACCEPTED ||
                    warrantyClaimGetById?.status === WarrantyClaimStatus.REJECTED
                }
                packageId={editingPart?.packageId}
                onUpdateSuccess={() => {
                    // Update işleminden sonra liste verilerini yenile
                    refetch();
                }}
                dealerId={dealerId}
                dealerAddressId={dealerAddressId}
            />

            <ItemReplacementDetailsDialog
                visible={replacementDetailsVisible}
                onHide={() => {
                    setReplacementDetailsVisible(false);
                    setSelectedReplacementRow(null);
                }}
                data={replacementDetailsData}
                chassisNo={warrantyClaimGetById?.chassisNo}
                itemCode={selectedReplacementRow?.itemCode}
                isLoading={isLoadingReplacementDetails}
            />

            <MaterialCostTypeDialog
                visible={materialCostTypeDialogVisible}
                onHide={() => {
                    setMaterialCostTypeDialogVisible(false);
                    setSelectedMaterialCostTypeRow(null);
                }}
                itemId={selectedMaterialCostTypeRow?.id}
                onSuccess={() => refetch()}
            />
        </div>
    );
};

export default PartListTable;
